import * as React from "react";
import "./TermsAndConditionsModal.css";
import * as cn from "classnames";

export interface TermsAndConditionsModalProps {
    onCancel: () => void;
    onAccept: (status: boolean) => void;
}

export interface TermsAndConditionsModalState {
    canAccept: boolean;
}

export class TermsAndConditionsModal extends React.Component<
    TermsAndConditionsModalProps,
    TermsAndConditionsModalState
> {
    constructor(props: TermsAndConditionsModalProps) {
        super(props);
        this.state = {
            canAccept: false
        };
    }

    handleScroll(e: any) {
        const scrollHeight = e.target.scrollHeight;
        const currentScrollPosition = e.target.scrollTop;
        const scrollWidth = e.target.scrollWidth;

        if (scrollHeight - currentScrollPosition - scrollWidth < 200) {
            this.setState({
                canAccept: true
            });
        } else {
            this.setState({
                canAccept: false
            });
        }
    }

    render() {
        const { onCancel, onAccept } = this.props;
        const { canAccept } = this.state;

        return (
            <div className="TermsAndCond">
                <div className="TermsAndCond_text" onScroll={(e: any) => this.handleScroll(e)}>
                    <h3 className="TermsAndCond_title">General Terms &amp; Conditions</h3>
                    <p className="TermsAndCond_paragraph">
                        These Terms &amp; Conditions (the “Terms”), which govern the use of the Service, have been
                        entered into between the customer (the “Customer”) registering an account on direct.adctrl.com
                        (the “Site”) and Adello Group AG (“Adello”), a company incorporated under the laws of
                        Switzerland with its head quarters in Zurich, Switzerland.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        BY CLICKING THE “ACCEPT” BUTTON IN THE ORDERING PROCESS OR SIGNING AN ORDER FORM THAT REFERS TO
                        THESE TERMS (THE “AGREEMENT”), YOU AGREE TO THE TERMS AND ANY APPLICABLE ORDER. IF YOU ARE
                        ENTERING INTO THIS AGREEMENT ON BEHALF OF A COMPANY OR OTHER LEGAL ENTITY, YOU REPRESENT THAT
                        YOU HAVE THE AUTHORITY TO BIND SUCH ENTITY TO THIS AGREEMENT, IN WHICH CASE THE TERMS
                        “CUSTOMER”, “YOU” OR “USER” SHALL REFER TO SUCH ENTITY.
                    </p>
                    <h3 className="TermsAndCond_title">1 DEFINITIONS</h3>
                    <p className="TermsAndCond_paragraph">
                        1.1 The following terms shall have the meaning ascribed to them below:
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Ad”</b> means the product of the ad, and placement created with the Content and published on
                        the Third Party Site.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Ad Campaign”</b> means an advertising campaign created using the Service, containing
                        optimizing goals, targeting settings and advertising strategies.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Click”</b> means the click on an Ad.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Content”</b> means all texts, graphics, videos, pictures and all other information, such as
                        the user generated ads, that the Customer, Users or anyone else uploads and/or publishes or in
                        other ways makes available via the Service.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Customer”</b> means the entity registering an account on the Site.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Features”</b> means features within the Service, such as launching a new Ad Campaign and
                        creating Ads by using a third party application provided within the Service.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Impression”</b> is when an Ad is fetched from the Third Party Sites, and is countable.
                        Whether the Ad is clicked is not taken into account. Each time an Ad is fetched, it is counted
                        as one impression.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Landing”</b> means a visit on the landing page after the Click.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Refunds”</b> means all refunds that will be paid out if the Ad Campaign does not deliver the
                        full amount of impressions booked using the Service.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Service”</b> means the Service, as described in clause 2.1, provided on the Site with its
                        current Features on the day of signing of the Terms.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Session”</b> means a measurement of visitor engagement that groups together analytics
                        activities taken by a visitor after the Landing.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Site”</b> means{" "}
                        <a className="TermsAndCond_link" href="https://direct.adctrl.com/" target="_blank">
                            direct.adctrl.com
                        </a>
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Third Party Services”</b> means any service within the Service not provided or owned by
                        Adello, such as Third Party Sites, or Financial Service Providers.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Third Party Sites”</b> means websites which are being used while using the Services, but are
                        not owned or controlled by Adello or the Customer.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“User”</b> means any authorized user of the Service.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        <b>“Viewable Impression”</b> means an impression which where viewable when served (unlike
                        impressions which were fetched from a Third Party Site, but never appeared in the visible part
                        of the browser / app).
                    </p>
                    <h3 className="TermsAndCond_title">2 THE SERVICE</h3>
                    <p className="TermsAndCond_paragraph">
                        2.1 The Service is an online real time self-service advertising application platform which
                        allows the Customer to create Ads by uploading or creating Content and buy ad spaces from third
                        parties through an automated process in order to place Ad Campaigns, to enable audience
                        targeting, to analyze the results in real time, and use these results to optimize future Ad
                        Campaigns. The Content is published on Third Party Sites.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        2.2 In the process of launching an Ad Campaign, the User will have to set a budget regarding the
                        preferred outcome with respect to Impressions, Clicks, Landings, and Sessions. The Customer
                        acknowledges and agrees that the budget set for the result of the Impressions is an estimate and
                        that Adello assumes no responsibility for the results being achieved.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        2.3 The Customer shall use the Site, Third Party Sites and the Service in accordance with the
                        Terms and instructions from Site and applicable laws, rules and regulations. The Customer shall
                        furthermore ensure that each of its Users accepts and complies with the Terms.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        2.4 Adello reserves the right to modify, suspend, and/or discontinue the use of the Service at
                        any time, with or without notice. All new functionality, features or services introduced to the
                        Service will be subject to the Terms. Adello will make reasonable efforts to keep the Service
                        operational and fully functional during updates.
                    </p>
                    <h3 className="TermsAndCond_title">3 USER ACCOUNTS</h3>
                    <p className="TermsAndCond_paragraph">
                        3.1 In order to obtain an account to the Service, the Customer has to register a User Account in
                        accordance with the at each time applicable instructions on the Site, Third Party Sites or
                        Adello.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        3.2 The Customer is solely responsible and liable for all access to and all actions and
                        activities conducted under the User Account, as well as its Users’ use of the Service. The
                        Customer shall immediately inform Adello about any unauthorized use of its User Accounts.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        3.3 Adello reserves the right to suspend any User or terminate any User Account if activities
                        occur which constitutes or may constitute a violation of the Terms, instructions by Adello, or
                        of any applicable local or international laws, rules or regulations.
                    </p>
                    <h3 className="TermsAndCond_title">4 USE OF DATA</h3>
                    <p className="TermsAndCond_paragraph">
                        4.1 The Customer acknowledges and agrees that Adello shall maintain the right to store and share
                        with the Site, Third Party Sites, and Third Party Services all User Account information
                        regarding the Customer, first name, last name, email address, company name, phone number,
                        address including country, city, postal code, street, website, billing contact name, email, VAT
                        number, and Ad Campaign information in an anonymized, consolidated and aggregated manner.
                    </p>
                    <h3 className="TermsAndCond_title">5 PAYMENT &amp; FEES</h3>
                    <p className="TermsAndCond_paragraph">
                        5.1 The Customer will pay for the cost of each Ad Campaign that is purchased via the Service by
                        invoice.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        5.2 In case an Ad Campaign is being cancelled by Site, Third Party Site or Adello, a refund will
                        be paid to the Customer. The refund will be calculated on the amount of impressions left for the
                        Ad Campaign booked period.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        5.3 In case an Ad Campaign has underdelivered the impressions which were booked by the Customer,
                        a refund will be paid to the Customer. The refund will be calculated on the amount of
                        impressions left for the Ad Campaign booked period. 5.4 The Customer acknowledges and agrees
                        that the Site, Third Party Site or Adello will be held harmless from any and all refund, claims,
                        damages, liabilities, costs and fees (including reasonable legal fees) relating to the Ad
                        Campaigns’ lack of delivery. In no event shall the Customer hold Site, Third Party Site or
                        Adello responsible or liable for any non-delivery, delay or similar in Ad Campaigns etc. For
                        reasons of clarity, this also applies to claims in connection with the loss of business
                        opportunities for the customer.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        5.4 The Customer acknowledges and agrees that the Site, Third Party Site or Adello will be held
                        harmless from any and all refund, claims, damages, liabilities, costs and fees (including
                        reasonable legal fees) relating to the Ad Campaigns’ lack of delivery. In no event shall the
                        Customer hold Site, Third Party Site or Adello responsible or liable for any non-delivery, delay
                        or similar in Ad Campaigns etc. For reasons of clarity, this also applies to claims in
                        connection with the loss of business opportunities for the customer.
                    </p>
                    <h3 className="TermsAndCond_title">6 PROHIBITED USE OF THE SERVICE</h3>
                    <p className="TermsAndCond_paragraph">
                        6.1 The Customer shall use the Service for lawful purposes only. The Customer agrees not to use
                        the Service for posting, transmitting or otherwise distributing illegal or other inappropriate
                        material.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        6.2 The Customer agrees to, within the scope of the Service and in relation to Site, Third Party
                        Site or Adello, to: not defame, abuse, harass, threaten or otherwise violate the legal rights of
                        others, including Adello; not publish, post or in any other way express any topic, material or
                        information that is inappropriate, defamatory, infringing, obscene, pornographic, racist,
                        terrorist, politically slanted, indecent or unlawful; not contribute to destructive activities
                        such as dissemination of viruses, spam or any other activity that might harm Site, Third Party
                        Sites or Adello, the Service or Users in any way.
                    </p>
                    <h3 className="TermsAndCond_title">7 CONTENT</h3>
                    <p className="TermsAndCond_paragraph">
                        7.1 The Service includes functions for uploading, posting, linking and communicating and
                        otherwise making Content available for others. By uploading Content to the Service, the Customer
                        warrants that it is either the owner of the Content or that it holds a valid permission to such
                        Content from the appropriate right holder, meaning that the Costumer’s use of the content in no
                        way infringes the rights of any Third Party. Furthermore, the Customer warrants that the
                        Content, or the Customer’s use thereof, is in no way a violation of any national or
                        international legislation.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        7.2 The Customer may not advertise for or promote advertisements/content that can be categorized
                        as one of the following prohibited Advertisement categories:
                    </p>
                    <ul className="TermsAndCond_list">
                        <li>Pornography</li>
                        <li>Weapons</li>
                        <li>Drugs and drug paraphernalia</li>
                        <li>Illegal Products</li>
                        <li>Misleading Advertisements</li>
                        <li>
                            Illegal Advertisements (Advertisements that do not comply with all applicable local/national
                            laws).
                        </li>
                    </ul>
                    <br />
                    <p className="TermsAndCond_paragraph">Ads may not include content that:</p>
                    <br />
                    <ul className="TermsAndCond_list">
                        <li>
                            Promotes or includes misleading, fraudulent or deceptive content where the user is not
                            getting the advertised benefit such as fraudulent or non-existent sweepstakes, virus
                            protection or virus removal
                        </li>
                        <li>
                            Includes negative option marketing (i.e. content where a user may interpret that by failing
                            to take affirmative action, either to reject an offer or cancel an agreement, the user may
                            be charged for goods or services)
                        </li>
                        <li>
                            Contains misleading or deceiving information for purposes of collecting user’s personal
                            data, such as “you are a winner of a [XYZ] price, click here”, “your device is infected by a
                            virus, click here to clean”, “your battery needs an upgrade” or a similar misleading content
                        </li>
                        <li>
                            Contains, promotes, or links to sites/pages that promote hateful or disparaging concepts
                            towards any race, religion, gender, sexual orientation or nationality
                        </li>
                        <li>
                            Contains, promotes, or links to sites/pages that promote firearms, bombs, explosive subject
                            matter and other weapons or how-to guides for any of the foregoing
                        </li>
                        <li>
                            Contains, promotes, encourages or links to illegal activities, including, but not limited to
                            hacking, illegal drugs or copyright infringement
                        </li>
                        <li>
                            Contains, promotes or links to pornography, indecent or obscene content or graphic sexual
                            depictions
                        </li>
                        <li>
                            Facilitates or promotes illegal file-sharing (copyrighted music, copyright protected video,
                            or the equivalent)
                        </li>
                    </ul>
                    <br />
                    <p className="TermsAndCond_paragraph">
                        7.3 All Contents are subject to Adello’s approval. Adello reserves the right to reject or cancel
                        any Ads or Ad Campains, at any time, for any reason. Adello takes no responsibility for any
                        technical quality, content or design with which Ads may be associated, and Adello has no
                        obligation to monitor or review the Ads or the associated technical quality, content or design.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        7.4 Adello makes no representation or warranty as to the accuracy, timeliness, quality,
                        completeness, suitability or reliability of any information or data accessed on or through the
                        Service. No information obtained from Adello or the Service shall create any warranty if not
                        expressly stated in the Terms.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        7.5 Unless nothing else is agreed upon between the Customer and Site, Third Party Sites and
                        Adello, Adello undertakes to host all Content provided by the Costumer, meaning that the Ad
                        Campaign will be delivered from the Adello servers to Third Party Sites. In order for Adello to
                        do so, the Costumer grants Adello a worldwide, non-exclusive, royalty-free, transferable right
                        to distribute and display the Content, or any part of it, and the intellectual property rights
                        therein, for the duration of the Ad Campaign.
                    </p>
                    <h3 className="TermsAndCond_title">8 INTELLECTUAL PROPERTY</h3>
                    <p className="TermsAndCond_paragraph">
                        8.1 The Service and its original content, Features, functionality and design elements are and
                        will remain the exclusive property of either Adello or the owner of a Third Party Site. The
                        Customer’s use of the Service is limited to the rights granted to the Customer under these
                        Terms. Adello’s intellectual property may not be used in connection with any product or service
                        without prior written consent from Adello.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        8.2 The Site, Third Party Site and the Service or any portion thereof may not be reproduced,
                        duplicated, copied, sold, resold, visited or otherwise exploited for any purpose inconsistent
                        with the limited rights granted to the Customer under the Terms.
                    </p>
                    <h3 className="TermsAndCond_title">9 INDEMNIFICATION</h3>
                    <p className="TermsAndCond_paragraph">
                        9.1 The Customer shall indemnify and hold Adello, Site or Third Party Site harmless with respect
                        to all direct and indirect liability, losses, damages, costs or expenses caused, arising out of,
                        or in connection with (i) the Customer’s negligence, (ii) the Customer’s breach of the Terms, or
                        (iii) the Customer’s misuse of the Service.
                    </p>
                    <h3 className="TermsAndCond_title">10 LIMITATION OF LIABILITY</h3>
                    <p className="TermsAndCond_paragraph">
                        10.1 Adello does not guarantee uninterrupted, secure or error-free operation of the Service. The
                        Service is provided “as is” without warranties of any kind, whether expressed or implied,
                        including, but not limited to, implied warranties of merchantability, fitness for a particular
                        purpose, non-infringement or course of performance. Adello is not responsible for neither
                        technical, hardware or software malfunctions, nor lost or unavailable network connections,
                        downtime or disconnections from User Accounts.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        10.2 The Customer is at all times responsible for the Content uploaded or otherwise made
                        available by its Users. This means that Adello is not responsible for the Content or the use
                        thereof. Furthermore, Adello assumes no responsibility for the content, advertising, goods or
                        services, privacy policies or other practices of the Site or Third Party Sites that may be
                        reached by links presented in the Service.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        10.3 The optimization of Ad Campaign is partly based on information which is provided by Third
                        Party Services. Adello takes no responsibility for the accuracy of such information.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        10.4 Adello is not responsible for any damage, loss, or injury resulting from hacking,
                        tampering, or other unauthorized access or use of the Service or User Accounts.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        10.5 To the extent permitted by applicable law, Adello shall under no circumstances be liable
                        for any indirect, incidental, special, consequential or exemplary damages, howsoever caused or
                        under any theory of liability, arising out of or in connection with the Terms. This includes,
                        but is not limited to, loss of profits, goodwill or business reputation, loss of data, costs of
                        procuring substitute goods or services, or other intangible losses.
                    </p>
                    <h3 className="TermsAndCond_title">11 BREACH OF THE TERMS</h3>
                    <p className="TermsAndCond_paragraph">
                        11.1 Should the Customer use the Service in violation of the Terms, Adello, Site or Third Party
                        Site shall have the right to: i) delete any Content produced by the Customer, ii) suspend and/or
                        terminate the Customers User Account iii) terminate the Agreement, and iv) receive a reasonable
                        compensation for its losses connected to the Customer’s violation.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        11.2 Adello, Site or Third Party Site reserves the right to directly limit the use of or access
                        to the Service and to block, restrict or delete any Content at any time, for any reason and
                        without liability, if such use, access or Content constitutes or may constitute i) a violation
                        of the Terms in general or of any applicable local or international laws, rules or regulations,
                        or ii) a risk of harming Adello, Site or Third Party Site trademarks, goodwill or reputation.
                    </p>
                    <h3 className="TermsAndCond_title">12 CHANGES TO &amp; ASSIGNMENT OF THE TERMS</h3>
                    <p className="TermsAndCond_paragraph">
                        12.1 Adello may, at any time and for any reason, amend the Terms by publishing the amended Terms
                        on the Site. The amended Terms shall automatically be effective upon publishing.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        12.2 Neither the Terms nor any obligation or right hereunder may be assigned or transferred by
                        the Customer without the prior written consent of Adello.
                    </p>
                    <h3 className="TermsAndCond_title">13 DURATION AND CANCELLATION</h3>
                    <p className="TermsAndCond_paragraph">
                        13.1 The Terms shall commence and remain in force as long as the Customer has a registered User
                        Account.
                    </p>
                    <h3 className="TermsAndCond_title">14 GOVERNING LAW</h3>
                    <p className="TermsAndCond_paragraph">
                        14.1 The Terms shall be construed in accordance with, and governed by, Swiss law. Disputes
                        arising in connection with the Terms shall be settled by the courts in Zurich, Switzerland.
                    </p>
                    <h3 className="TermsAndCond_title">15 PRIVACY &amp; CONFIDENTIALITY</h3>
                    <p className="TermsAndCond_paragraph">
                        15.1 Customer agrees to the terms of Adello privacy policy at
                        https://www.adello.com/about-us/privacy-policy/ which may be modified by Adello in its
                        reasonable discretion from time to time. During the course of this Agreement, information that
                        is confidential or proprietary may be disclosed between us including but not limited to:
                        software, technical processes, formulas, source code, sales, costs, other unpublished financial
                        information, product and business plans, advertising revenues, usage rates, advertising
                        relationships, projections, and marketing data.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        15.2 Except as provided below, neither Customer nor Adello will make any disclosure of the other
                        party’s confidential information to anyone other than their employees or contractors who have a
                        need to know such information in connection with this Agreement, and who are bound by written
                        agreements to protect the confidentiality of such information. Customer and Adello will notify
                        their employees and contractors of their confidentiality obligations hereunder.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        15.3 Confidential Information shall not include information that the receiving party can
                        demonstrate (i) is, at the time of disclosure, or thereafter becomes, part of the public domain
                        through a source other than the receiving party, (ii) was known to the receiving party at the
                        time of disclosure, (iii) is independently developed by the receiving party, or (iv) is
                        subsequently learned from a third party not under a confidentiality obligation to the providing
                        party.
                    </p>
                    <h3 className="TermsAndCond_title">16 GENERAL</h3>
                    <p className="TermsAndCond_paragraph">
                        16.1 Adello may give notice by means of email to Customers or Users e-mail address, or by
                        written communication sent by first class mail or pre-paid post to Customers address on record
                        in Adello account information. Customer may give notice to Adello at any time by email to
                        info@adello.com, letter delivered by nationally recognized overnight delivery service or first
                        class postage, prepaid mail to Adello, in either case, addressed to the attention of: CEO. Any
                        such notice shall be deemed to have been given upon the expiration of 48 hours after mailing or
                        posting (if sent by first class mail or pre-paid post) or 12 hours after sending (if sent by
                        email).
                    </p>
                    <p className="TermsAndCond_paragraph">
                        16.2 In case any one or more of the provisions contained in this Agreement is for any reason
                        held to be invalid, illegal or unenforceable in any respect, such invalidity, illegality, or
                        unenforceability shall not affect any other provision of this Agreement, and such invalid,
                        illegal, or unenforceable provision shall be reformed and construed so that it will be valid,
                        legal, and enforceable to the maximum extent permitted by law.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        16.3 Nothing in this Agreement is intended to, or shall be deemed to, establish any partnership
                        or joint venture between the Customer and Adello.
                    </p>
                    <p className="TermsAndCond_paragraph">
                        16.4 The failure of either party to enforce any right or provision in this Agreement shall not
                        constitute a waiver of such right or provision unless acknowledged and agreed to by the parties
                        in writing.{" "}
                    </p>
                    <p className="TermsAndCond_paragraph">
                        16.5 This Agreement, together with any applicable Order and any other document referenced
                        herein, comprises the entire agreement between the Customer and Adello and supersedes all prior
                        or contemporaneous negotiations, discussions or agreements, whether written or oral, between the
                        parties regarding the subject matter contained herein.
                    </p>
                </div>

                <button className="common-btn TermsAndCond_btn" onClick={() => onCancel()} type="button">
                    Cancel
                </button>
                <button
                    className={cn("common-btn TermsAndCond_btn", canAccept ? "" : "disabled")}
                    onClick={() => onAccept(true)}
                    type="button"
                    disabled={!canAccept}
                >
                    Accept
                </button>
            </div>
        );
    }
}
